import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";
import { StarBadge } from "@/components/Stars";
import ListingCard from "@/components/ListingCard";
import { ShieldCheck, MapPin } from "@phosphor-icons/react";

export default function HostProfile() {
  const { id } = useParams();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/hosts/${id}`).then((r) => setProfile(r.data)).finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-12 space-y-6">
        <Skeleton className="h-24 w-96"/>
        <Skeleton className="h-64 w-full"/>
      </div>
    );
  }
  if (!profile) return <div className="p-16 text-center text-neutral-500">Host not found</div>;

  const { host, listings, avg_rating, review_count, listing_count } = profile;

  return (
    <div className="max-w-7xl mx-auto px-6 py-12">
      {/* Hero */}
      <div className="border border-neutral-200 p-8 md:p-12 grid md:grid-cols-12 gap-8 items-center mb-10">
        <div className="md:col-span-3 flex justify-center">
          {host.profile_photo ? (
            <img src={host.profile_photo} alt="" className="w-32 h-32 rounded-full object-cover border border-neutral-200"/>
          ) : (
            <div className="w-32 h-32 rounded-full bg-neutral-900 text-white text-4xl flex items-center justify-center font-display">
              {host.name?.[0]}
            </div>
          )}
        </div>
        <div className="md:col-span-6">
          <div className="label-eyebrow mb-2">Host</div>
          <h1 data-testid="host-name" className="font-display font-medium text-4xl sm:text-5xl tracking-tight">{host.name}</h1>
          <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-neutral-500">
            {host.id_verified && <span className="inline-flex items-center gap-1"><ShieldCheck size={14}/> ID verified</span>}
            {host.id_verified && <span className="text-neutral-300">·</span>}
            <StarBadge rating={avg_rating} count={review_count}/>
          </div>
        </div>
        <div className="md:col-span-3 grid grid-cols-2 gap-4 text-center md:text-left">
          <div>
            <div className="font-display text-3xl font-medium">{listing_count}</div>
            <div className="text-[11px] uppercase tracking-widest text-neutral-500 mt-1">Dens</div>
          </div>
          <div>
            <div className="font-display text-3xl font-medium">{avg_rating ? avg_rating.toFixed(1) : "—"}</div>
            <div className="text-[11px] uppercase tracking-widest text-neutral-500 mt-1">Avg rating</div>
          </div>
        </div>
      </div>

      {/* Listings */}
      <div className="mb-4 flex items-baseline justify-between">
        <div className="label-eyebrow">Dens by {host.name?.split(" ")[0]}</div>
        <Link to="/browse" className="text-sm underline text-neutral-500 hover:text-neutral-900">Browse all</Link>
      </div>
      {listings.length === 0 ? (
        <div className="border border-dashed border-neutral-300 p-16 text-center text-neutral-500 text-sm">
          {host.name} hasn't listed any dens yet.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {listings.map((l, i) => <ListingCard key={l.id} listing={l} index={i}/>)}
        </div>
      )}
    </div>
  );
}
